// src/features/records/components/RecordDetail/RecordDetailErrorBoundary.js
import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import styles from './RecordDetail.module.css';
import { logError, ErrorSeverity, ErrorCategory } from '../../../../services/errorService';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';

class RecordDetailErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RecordDetail error:', error, errorInfo);
    
    // Log to analytics for production monitoring
    logError(error, {
      severity: ErrorSeverity.CRITICAL, // Record detail errors are more critical
      category: ErrorCategory.RENDERING,
      component: 'RecordDetailErrorBoundary',
      action: 'componentDidCatch',
      metadata: {
        componentStack: errorInfo.componentStack,
        recordId: this.props.recordId || 'unknown'
      }
    });
    
    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorKey = mapRecordsErrorToKey(this.state.error, {
        fallbackKey: 'records.detail.boundary.message'
      });

      // Default error UI
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <AlertCircle className={styles.errorIcon} size={48} />
            <h2 className={styles.errorTitle}>
              {t('records.detail.boundary.title', 'Something went wrong')}
            </h2>
            <p className={styles.errorMessage}>
              {t(errorKey)}
            </p>
            <button 
              className={styles.errorRetryButton}
              onClick={this.handleRetry}
            >
              {t('common.retry', 'Retry')}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

RecordDetailErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  onError: PropTypes.func,
  fallback: PropTypes.node,
  recordId: PropTypes.string,
  t: PropTypes.func.isRequired
};

export default withTranslation()(RecordDetailErrorBoundary);
